/* assets/littleg-app-sidebar.js — MAPBOX CONSOLE RUNTIME
 * Sole runtime owner for Sidebar presentation state:
 * - Desktop: 264px expanded <-> 72px Mapbox Assembly Rail
 * - Mobile / Home / Tablet overlay: Temporary drawer + backdrop
 * - Subgroup progressive disclosure: "See all" / "Show less"
 * - MapGPT Quick Search Trigger (⌘K / Ctrl+K)
 */
(function () {
  'use strict';

  var VERSION = '2026-10-30-MAPBOX-CONSOLE-22';
  var ROOT_SELECTOR = '[data-littleg-app-sidebar]';
  var DESKTOP_TOGGLE_SELECTOR = '[data-littleg-sidebar-toggle]';
  var MOBILE_TOGGLE_SELECTOR = '[data-littleg-mobile-menu-toggle], [data-sidebar-launcher]';
  var BACKDROP_SELECTOR = '[data-littleg-sidebar-backdrop]';
  var SUBGROUP_SELECTOR = '[data-sidebar-subgroup]';
  var SEE_ALL_SELECTOR = '[data-sidebar-see-all]';
  const SIDEBAR_STATE_KEY = 'littleg:ui:sidebar-state:v2';

  var TABLET_QUERY = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
  var DESKTOP_QUERY = window.matchMedia('(min-width: 1200px)');
  var MOBILE_QUERY = window.matchMedia('(max-width: 767px)');
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * @type {Window & {
   *   __LITTLEG_APP_SIDEBAR_RUNTIME__?: string,
   *   __LITTLEG_APP_SIDEBAR_BOUND__?: string
   * }}
   */
  const sidebarWindow = window;

  if (sidebarWindow.__LITTLEG_APP_SIDEBAR_RUNTIME__ === VERSION) return;
  sidebarWindow.__LITTLEG_APP_SIDEBAR_RUNTIME__ = VERSION;

  /** @type {Element | null} */
  var lastTriggerNode = null;
  /** @type {Array<{node: Element, wasInert: boolean}>} */
  var publicBackgroundNodes = [];

  function getRootNode() {
    return document.querySelector(ROOT_SELECTOR);
  }

  /** @param {string} key */
  function safeGetStorage(key) {
    try { return window.localStorage.getItem(key) || ''; } catch (_) { return ''; }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  function safeSetStorage(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  }


  function isMobile() {
    return MOBILE_QUERY.matches;
  }
  function isTablet() {
    return TABLET_QUERY.matches;
  }
  function isDesktop() {
    return DESKTOP_QUERY.matches;
  }

  function isHome() {
    var body = document.body;
    return !!(
      body &&
      (body.classList.contains('template-index') || body.getAttribute('data-template') === 'index')
    );
  }

  function usesHomeDrawer() {
    var body = document.body;
    return !!(
      body &&
      (isHome() ||
        body.getAttribute('data-opening-gate-route') === 'true' ||
        body.classList.contains('is-littleg-friend-map-surface'))
    );
  }

  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (node) {
      // [Pro Refinement] Use offsetWidth/Height instead of getClientRects() to avoid forced synchronous layout
      return (
        node.tabIndex >= 0 &&
        !node.matches(':disabled') &&
        !node.closest('[hidden], [inert], [aria-hidden="true"]') &&
        (node.offsetWidth > 0 || node.offsetHeight > 0)
      );
    });
  }

  function ensureBackdropNode() {
    var backdrop = document.querySelector(BACKDROP_SELECTOR);
    var sidebar = getRootNode();
    var closeLabel = sidebar && sidebar.getAttribute('data-sidebar-close-label');
    var closeControl = sidebar && sidebar.querySelector('[data-sidebar-drawer-close]');
    if (!closeLabel) closeLabel = closeControl && closeControl.getAttribute('aria-label');
    if (!closeLabel) closeLabel = 'Close navigation';
    if (backdrop) {
      backdrop.setAttribute('aria-label', closeLabel);
      return backdrop;
    }

    backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'littleg-sidebar-backdrop';
    backdrop.setAttribute('data-littleg-sidebar-backdrop', '');
    backdrop.setAttribute('aria-label', closeLabel);
    backdrop.hidden = true;
    // [Pro Refinement] Prevent iOS background scroll bleed when dragging on backdrop
    backdrop.addEventListener(
      'touchmove',
      function (e) {
        if (e.cancelable) e.preventDefault();
      },
      { passive: false }
    );
    document.body.appendChild(backdrop);
    return backdrop;
  }

  function setPublicModalState(open) {
    var sidebar = getRootNode();
    const usesModalBackground = !!sidebar;
    if (open) {
      if (!usesModalBackground) return;
      sidebar.setAttribute('role', 'dialog');
      sidebar.setAttribute('aria-modal', 'true');
      if (publicBackgroundNodes.length) return;
      var backdrop = document.querySelector(BACKDROP_SELECTOR);
      Array.from(document.body.children).forEach(function (node) {
        if (
          node === sidebar ||
          node === backdrop ||
          node.contains(sidebar) ||
          node.contains(backdrop)
        )
          return;
        var wasInert = node.hasAttribute('inert');
        publicBackgroundNodes.push({ node: node, wasInert: wasInert });
        node.setAttribute('inert', '');
        if ('inert' in node) node.inert = true;
      });
      return;
    }
    if (sidebar && (usesModalBackground || publicBackgroundNodes.length)) {
      sidebar.removeAttribute('role');
      sidebar.removeAttribute('aria-modal');
    }
    publicBackgroundNodes.forEach(function (item) {
      if (!item.wasInert) {
        item.node.removeAttribute('inert');
        if ('inert' in item.node) item.node.inert = false;
      }
    });
    publicBackgroundNodes = [];
  }

  function setDrawerInteractiveState(open) {
    var sidebar = getRootNode();
    if (!sidebar) return;

    if (open || (!isMobile() && !usesHomeDrawer())) {
      sidebar.removeAttribute('inert');
      sidebar.removeAttribute('aria-hidden');
      if ('inert' in sidebar) sidebar.inert = false;
    } else {
      sidebar.setAttribute('inert', '');
      sidebar.setAttribute('aria-hidden', 'true');
      if ('inert' in sidebar) sidebar.inert = true;
    }
  }

  function notifyMapCanvasReflow(state) {
    window.dispatchEvent(
      new CustomEvent('littleg:sidebar-state', {
        detail: {
          state: state,
          isMobile: isMobile(),
          isTablet: isTablet(),
          isHome: isHome(),
          source: VERSION,
        },
      })
    );

    // [Pro Refinement] Respect prefers-reduced-motion: zero delay if system motion is reduced
    var prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var delay = prefersReducedMotion ? 0 : 300;

    window.setTimeout(function () {
      window.dispatchEvent(new Event('resize'));
    }, delay);
  }

  function syncMobileToggleButtons(open) {
    document.querySelectorAll(MOBILE_TOGGLE_SELECTOR).forEach(function (button) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = button.getAttribute(open ? 'data-label-close' : 'data-label-open');
      if (label) button.setAttribute('aria-label', label);
    });
  }

  function syncDesktopToggleButtons(state) {
    const drawerMode = usesHomeDrawer() || isTablet();

    document.querySelectorAll(DESKTOP_TOGGLE_SELECTOR).forEach(function (button) {
      const expanded = drawerMode ? isDrawerOpen() : state === 'expanded';

      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      const sidebar = button.closest(ROOT_SELECTOR);
      const launcher = document.querySelector('[data-sidebar-launcher="mobile"]');
      const drawerCloseLabel = sidebar && sidebar.getAttribute('data-sidebar-close-label');
      const drawerOpenLabel = launcher && launcher.getAttribute('data-label-open');

      const label = drawerMode
        ? expanded
          ? drawerCloseLabel
          : drawerOpenLabel
        : button.getAttribute(expanded ? 'data-label-collapse' : 'data-label-expand');

      if (label) {
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      }
    });
  }

  function closeTemporaryDrawer(restoreFocus) {
    var body = document.body;
    var sidebar = getRootNode();
    var backdrop = document.querySelector(BACKDROP_SELECTOR);
    if (!body) return;

    body.setAttribute('data-littleg-mobile-nav', 'closed');
    body.setAttribute('data-littleg-tablet-nav', 'closed');
    body.classList.remove('littleg-sidebar-drawer-open');
    if (sidebar) sidebar.classList.remove('is-open');

    syncMobileToggleButtons(false);
    if (isTablet() && !usesHomeDrawer()) syncDesktopToggleButtons('collapsed');

    if (backdrop) {
      backdrop.hidden = true;
      backdrop.classList.remove('is-visible');
      backdrop.setAttribute('aria-hidden', 'true');
    }

    setDrawerInteractiveState(false);
    setPublicModalState(false);

    if (
      restoreFocus &&
      (lastTriggerNode instanceof HTMLElement ||
        lastTriggerNode instanceof SVGElement) &&
      document.contains(lastTriggerNode)
    ) {
      try { lastTriggerNode.focus({ preventScroll: true }); } catch (_) { lastTriggerNode.focus(); }
    }
    lastTriggerNode = null;
  }

  function openTemporaryDrawer(triggerNode) {
    var sidebar = getRootNode();
    var body = document.body;
    if (!sidebar || !body) return;

    var backdrop = ensureBackdropNode();
    lastTriggerNode = triggerNode || document.activeElement;

    document.dispatchEvent(
      new CustomEvent('littleg:overlay:close-others', {
        detail: { except: 'sidebar-drawer', source: VERSION },
      })
    );

    if (isMobile() || usesHomeDrawer()) body.setAttribute('data-littleg-mobile-nav', 'open');
    if (isTablet() && !usesHomeDrawer()) {
      body.setAttribute('data-littleg-tablet-nav', 'open');
      syncDesktopToggleButtons('expanded');
    }
    body.classList.add('littleg-sidebar-drawer-open');
    sidebar.classList.add('is-open');

    backdrop.hidden = false;
    backdrop.classList.add('is-visible');
    backdrop.setAttribute('aria-hidden', 'false');

    setDrawerInteractiveState(true);
    setPublicModalState(true);
    syncMobileToggleButtons(true);

    window.requestAnimationFrame(function () {
      var preferredCloseBtn =
        isTablet() && !usesHomeDrawer()
          ? sidebar.querySelector(DESKTOP_TOGGLE_SELECTOR)
          : sidebar.querySelector('.littleg-app-sidebar__rail-toggle--mobile');
      // [Pro Refinement] Avoid forced layout reflow during drawer transition
      if (
        preferredCloseBtn &&
        !(preferredCloseBtn.offsetWidth > 0 || preferredCloseBtn.offsetHeight > 0)
      )
        preferredCloseBtn = null;
      var firstFocusable = preferredCloseBtn || getFocusableElements(sidebar)[0];
      if (firstFocusable) {
        try {
          firstFocusable.focus({ preventScroll: true });
        } catch (_) {
          firstFocusable.focus();
        }
      }
    });
  }

  function isDrawerOpen() {
    var body = document.body;
    if (!body) return false;
    return (
      body.getAttribute('data-littleg-mobile-nav') === 'open' ||
      body.getAttribute('data-littleg-tablet-nav') === 'open'
    );
  }

  function getPreferredDesktopState() {
    const saved = safeGetStorage(SIDEBAR_STATE_KEY);
    return saved === 'collapsed' || saved === 'expanded' ? saved : 'collapsed';
  }

  function applyViewportState() {
    var sidebar = getRootNode();
    var body = document.body;
    if (!sidebar || !body) return;

    closeTemporaryDrawer(false);

    var state = 'mobile';
    if (usesHomeDrawer()) {
      state = 'home-drawer';
    } else if (isDesktop()) {
      state = getPreferredDesktopState();
    } else if (isTablet()) {
      state = 'collapsed';
    }

    document.documentElement.setAttribute('data-littleg-sidebar-state', state);
    body.setAttribute('data-littleg-sidebar-state', state);
    sidebar.setAttribute('data-sidebar-state', state);

    if (isMobile() || usesHomeDrawer()) {
      setDrawerInteractiveState(false);
      syncMobileToggleButtons(false);
    } else {
      setDrawerInteractiveState(true);
    }

    syncDesktopToggleButtons(state);
    notifyMapCanvasReflow(state);
  }

  function toggleDesktopRail(triggerNode) {
    var body = document.body;
    var sidebar = getRootNode();
    if (!body || !sidebar) return;

    if (usesHomeDrawer() || isTablet()) {
      if (isDrawerOpen()) closeTemporaryDrawer(true);
      else openTemporaryDrawer(triggerNode);
      return;
    }

    if (!isDesktop()) return;

    var current =
      body.getAttribute('data-littleg-sidebar-state') === 'collapsed' ? 'collapsed' : 'expanded';
    var next = current === 'expanded' ? 'collapsed' : 'expanded';

    body.setAttribute('data-littleg-sidebar-state', next);
    document.documentElement.setAttribute('data-littleg-sidebar-state', next);
    sidebar.setAttribute('data-sidebar-state', next);

    safeSetStorage(SIDEBAR_STATE_KEY, next);
    syncDesktopToggleButtons(next);
    notifyMapCanvasReflow(next);
  }

  /* Mapbox Subgroup Progressive Disclosure ("See all" / "Show less") */
  function syncSubgroups(root) {
    var subgroups = Array.from((root || document).querySelectorAll(SUBGROUP_SELECTOR));
    subgroups.forEach(function (subgroup, index) {
      if (!subgroup.id) subgroup.id = 'littleg-sidebar-subgroup-' + (index + 1);
      var extras = subgroup.querySelectorAll('[data-sidebar-extra]');
      var toggle = subgroup.querySelector(SEE_ALL_SELECTOR);
      if (!toggle) return;

      if (!extras.length) {
        toggle.hidden = true;
        return;
      }

      toggle.hidden = false;
      var expanded = subgroup.classList.contains('is-expanded');
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.setAttribute('aria-controls', subgroup.id);

      // [Pro Refinement] Explicitly hide collapsed extra items from screen readers
      extras.forEach(function (extra) {
        if (expanded) {
          extra.removeAttribute('aria-hidden');
        } else {
          extra.setAttribute('aria-hidden', 'true');
        }
      });

      const label = toggle.querySelector('[data-sidebar-see-all-label]');
      if (label) {
        const language = document.documentElement.lang.toLowerCase();
        const isChinese = /^zh(?:-|$)/.test(language);
        const isSimplified = /^zh-(?:cn|hans|sg)(?:-|$)/.test(language);

        label.textContent = isChinese
          ? expanded
            ? '收起'
            : isSimplified
              ? '展开更多'
              : '展開更多'
          : expanded
            ? 'Show less'
            : 'See all';
      }
    });
  }

  function handleSubgroupToggle(toggle) {
    var subgroup = toggle.closest(SUBGROUP_SELECTOR);
    if (!subgroup) return;

    subgroup.classList.toggle('is-expanded');
    syncSubgroups(subgroup.parentElement);
  }

  function trapDrawerFocus(event) {
    if (event.key !== 'Tab' || !isDrawerOpen()) return;
    var sidebar = getRootNode();
    if (!sidebar) return;

    var items = getFocusableElements(sidebar);
    if (!items.length) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (!sidebar.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindEvents() {
    if (sidebarWindow.__LITTLEG_APP_SIDEBAR_BOUND__ === VERSION) return;
    sidebarWindow.__LITTLEG_APP_SIDEBAR_BOUND__ = VERSION;

    ensureBackdropNode();
    applyViewportState();
    syncSubgroups(document);
    syncLocalWorkspace();
    // History API calls have no native navigation event. Publish once from the
    // shared runtime; the existing product/legacy owners project their own UI.
    ['pushState', 'replaceState'].forEach(function (method) {
      var original = window.history[method];
      if (typeof original !== 'function') return;
      window.history[method] = function () {
        var result = original.apply(this, arguments);
        window.dispatchEvent(new CustomEvent('littleg:sidebar-location-change'));
        return result;
      };
    });
    window.addEventListener('littleg:sidebar-location-change', syncLocalWorkspace);
    window.addEventListener('popstate', syncLocalWorkspace);
    /* Ctrl/Command+K is owned by assets/main-search.js. */

    document.addEventListener('click', function (event) {
      // 1. See all Progressive Disclosure Toggle
      var seeAllToggle = event.target.closest ? event.target.closest(SEE_ALL_SELECTOR) : null;
      if (seeAllToggle) {
        event.preventDefault();
        event.stopPropagation();
        handleSubgroupToggle(seeAllToggle);
        return;
      }

      // NAV-00: fixed Account slot invokes existing GTender native auth owner.
      var footerAccount = event.target.closest
        ? event.target.closest('[data-nav00-native-account]')
        : null;
      if (footerAccount) {
        event.preventDefault();
        var headerAuth = document.querySelector('.globalnav [data-gtender-native-auth-open]');
        if (headerAuth) headerAuth.click();
        return;
      }

      // 2. Desktop Rail Toggle (Expanded <-> Collapsed)
      var desktopToggle = event.target.closest
        ? event.target.closest(DESKTOP_TOGGLE_SELECTOR)
        : null;
      if (desktopToggle) {
        event.preventDefault();
        toggleDesktopRail(desktopToggle);
        return;
      }

      // Explicit close controls never reopen the drawer.
      const drawerClose = event.target.closest
        ? event.target.closest('[data-sidebar-drawer-close]')
        : null;

      if (drawerClose) {
        event.preventDefault();
        closeTemporaryDrawer(true);
        return;
      }

      // Mobile / Home launcher.
      var mobileToggle = event.target.closest ? event.target.closest(MOBILE_TOGGLE_SELECTOR) : null;
      if (mobileToggle) {
        event.preventDefault();
        if (isDrawerOpen()) closeTemporaryDrawer(true);
        else openTemporaryDrawer(mobileToggle);
        return;
      }

      // 4. Backdrop Click
      var backdrop = event.target.closest ? event.target.closest(BACKDROP_SELECTOR) : null;
      if (backdrop) {
        event.preventDefault();
        closeTemporaryDrawer(true);
        return;
      }

      // 5. Normal Link Navigation (Close temporary drawer on mobile/home)
      var sidebarLink = event.target.closest
        ? event.target.closest(ROOT_SELECTOR + ' a[href]')
        : null;
      if (sidebarLink && (isMobile() || isTablet() || usesHomeDrawer())) {
        // [Pro Refinement] Preserve drawer state if link opens in new tab (_blank) or targets an anchor (#)
        const href = sidebarLink.getAttribute('href') || '';
        const isNewTab = sidebarLink.getAttribute('target') === '_blank';
        const isAnchor = href.indexOf('#') === 0;
        const isModifiedClick =
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

        if (!event.defaultPrevented && !isModifiedClick && !isNewTab && !isAnchor) {
          closeTemporaryDrawer(false);
        }
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!isDrawerOpen() || event.defaultPrevented) return;

      const sidebar = getRootNode();
      const topModal = Array.from(
        document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"]')
      ).find(
        (node) =>
          node !== sidebar &&
          !node.closest('[hidden], [inert], [aria-hidden="true"]') &&
          (node.offsetWidth > 0 || node.offsetHeight > 0)
      );

      // Let an active dialog own both Escape and Tab.
      if (topModal) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeTemporaryDrawer(true);
        return;
      }
      trapDrawerFocus(event);
    });

    document.addEventListener('littleg:overlay:close-others', function (event) {
      if (event && event.detail && event.detail.except === 'sidebar-drawer') return;
      closeTemporaryDrawer(false);
    });

    var onViewportChange = function () {
      applyViewportState();
    };
    [MOBILE_QUERY, TABLET_QUERY, DESKTOP_QUERY].forEach(function (query) {
      if (typeof query.addEventListener === 'function')
        query.addEventListener('change', onViewportChange);
      else if (typeof query.addListener === 'function') query.addListener(onViewportChange);
    });

    document.addEventListener('shopify:section:load', function (event) {
      const scope = event.target;
      if (!scope || !scope.querySelectorAll) return;

      const containsSidebar =
        (scope.matches && scope.matches(ROOT_SELECTOR)) || scope.querySelector(ROOT_SELECTOR);

      if (containsSidebar) {
        applyViewportState();
      }

      syncSubgroups(scope);
    });
    window.addEventListener('pageshow', applyViewportState);
  }

  function syncLocalWorkspace() {
    var sidebar = getRootNode();
    if (!sidebar || sidebar.getAttribute('data-nav00-context') !== 'gcitypartners') return;
    var url = new URL(window.location.href);
    var workspace = /\/pages\/gcitypartners-dashboard\/?$/.test(url.pathname)
      ? url.searchParams.get('workspace')
      : '';
    sidebar.querySelectorAll('[data-sidebar-local-workspace]').forEach(function (link) {
      var active = link.getAttribute('data-sidebar-local-workspace') === workspace;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }

  bindEvents();
})();
